import { useState } from "react"
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import { SortableItem } from "lib/@components/SortableItem"
import { describeRateCardModifier } from "./describe-rate-card-modifier"
import { getCurrencySymbol } from "../format-utils"
import { formatRateCardModifierScope } from "./rate-card-modifier-scope"

export function RateCardModifierRow({
    item,
    index,
    modifiers,
    currency,
    tradeOptions,
    showScope,
    onEdit,
    onDelete,
    readOnly,
}) {
    const [dragProps, setDragProps] = useState({})
    const scopeLabel = formatRateCardModifierScope(item.scope, tradeOptions)
    const valueLabel = formatValue(item, currency)
    const applyToLabel = formatApplyTo(item)
    const description = describeRateCardModifier({ modifier: item, index, modifiers, tradeOptions, currency })

    return (
        <SortableItem id={item.id} setDragProps={setDragProps} disabled={readOnly}>
            <Box
                data-cy="rate-card-modifier-row"
                sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                }}
            >
                <ListItemBox sx={{ px: 2, py: 1.5, gap: 2 }}>
                    <If value={!readOnly}>
                        <Box sx={{ width: 32, flexShrink: 0, cursor: "grab" }} {...dragProps}>
                            <Iconify icon="mdi:drag-vertical" width={24} />
                        </Box>
                        <Else>
                            <Box sx={{ width: 32, flexShrink: 0 }} />
                        </Else>
                    </If>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontSize: 16 }} noWrap>
                            {item.name}
                        </Typography>
                        <Typography data-cy="modifier-description" variant="body2" color="text.secondary">
                            {description}
                        </Typography>
                    </Box>
                    <If value={showScope}>
                        <Box sx={{ width: 220 }} data-cy="modifier-scope">
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }} noWrap>
                                {scopeLabel}
                            </Typography>
                        </Box>
                    </If>
                    <Box sx={{ width: 140, textAlign: "right" }}>
                        <Typography data-cy="modifier-value" variant="body2" sx={{ fontSize: 16, fontWeight: 500 }}>
                            {valueLabel}
                        </Typography>
                    </Box>
                    <Box sx={{ width: 160 }}>
                        <If value={item.type === "percentage"}>
                            <Chip label={applyToLabel} size="small" variant="outlined" />
                        </If>
                    </Box>
                    <If value={!readOnly}>
                        <Box sx={{ width: 80, flexShrink: 0, display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                            <Tooltip title="Edit" placement="top">
                                <IconButton size="small" onClick={handleEdit} data-cy="modifier-edit">
                                    <Iconify icon="eva:edit-outline" width={20} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete" placement="top">
                                <IconButton size="small" onClick={handleDelete} data-cy="modifier-delete">
                                    <Iconify icon="eva:trash-2-outline" width={20} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Else>
                            <Box sx={{ width: 80, flexShrink: 0 }} />
                        </Else>
                    </If>
                </ListItemBox>
            </Box>
        </SortableItem>
    )

    function handleEdit() {
        onEdit(item)
    }

    function handleDelete() {
        onDelete(item)
    }
}

function formatApplyTo(item) {
    if (item.type !== "percentage") return "-"
    if (item.applyTo === "base") return "Base cost"
    return "Compound"
}

function formatValue(modifier, currency) {
    if (modifier.type === "percentage") {
        const pct = (modifier.value || 0) * 100
        const sign = pct >= 0 ? "+" : ""
        return `${sign}${formatNumber(pct)}%`
    }
    if (modifier.type === "fixed") {
        const value = Number(modifier.value) || 0
        const sign = value >= 0 ? "+" : "-"
        return `${sign}${getCurrencySymbol(currency)}${formatNumber(Math.abs(value))}`
    }
    return ""
}

function formatNumber(value) {
    return Number(value).toLocaleString("en-GB", { maximumFractionDigits: 2 })
}
